import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../Context/AuthProvider";
import { updateSecurityMaster } from "../../http/api";
import {
  FIELD_TYPES,
  SECURITY_FIELDS,
  isFieldEditable,
  toInputValue,
  validateField,
  buildModifiedPayload,
} from "./updateSecurityConfig";

// Resolve the SecMaster primary key from the row, tolerating a few likely names.
const resolveSecMasterId = (securityData) =>
  securityData?.secMasterId ??
  securityData?.id ??
  securityData?.securityMstId ??
  securityData?.secMasterMstId ??
  null;

/**
 * State / error / validation / submit management for the Update Security popup.
 * Mirrors the structure of useOrderEntry: the component stays presentational
 * and all of the logic lives here.
 */
export const useUpdateSecurity = ({ securityData, open, onClose, onUpdated }) => {
  const { token } = useAuth();

  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Partition fields once per record. Editability is locked to the value the
  // record had when the popup opened (see isFieldEditable).
  const editableFields = useMemo(
    () => SECURITY_FIELDS.filter((field) => isFieldEditable(field, securityData)),
    [securityData]
  );
  const disabledFields = useMemo(
    () => SECURITY_FIELDS.filter((field) => !isFieldEditable(field, securityData)),
    [securityData]
  );

  // Seed / reset the form whenever the popup opens with a (new) record.
  useEffect(() => {
    if (!open || !securityData) return;
    const initial = {};
    editableFields.forEach((field) => {
      initial[field.key] = toInputValue(field, securityData[field.key]);
    });
    setFormValues(initial);
    setErrors({});
    setSubmitError(null);
    setLoading(false);
  }, [open, securityData, editableFields]);

  const handleChange = useCallback((key, value) => {
    const field = editableFields.find((f) => f.key === key);
    if (field && field.type === FIELD_TYPES.NUMBER) {
      // Runtime: block negative input entirely
      if (typeof value === "string" && value.startsWith("-")) return;
      // Runtime: block values that exceed the field's max
      if (field.max !== undefined) {
        const num = parseFloat(value);
        if (Number.isFinite(num) && num > field.max) return;
      }
      // Runtime: block more decimal places than maxDecimals allows
      if (field.maxDecimals !== undefined && typeof value === "string") {
        const dotIdx = value.indexOf(".");
        if (dotIdx !== -1 && value.length - dotIdx - 1 > field.maxDecimals) return;
      }
    }
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
    setSubmitError(null);
  }, [editableFields]);

  const validateAll = useCallback(() => {
    const nextErrors = {};
    editableFields.forEach((field) => {
      const message = validateField(field, formValues[field.key]);
      if (message) nextErrors[field.key] = message;
    });
    setErrors(nextErrors);
    return nextErrors;
  }, [editableFields, formValues]);

  const handleSubmit = useCallback(async () => {
    const validationErrors = validateAll();
    const errorKeys = Object.keys(validationErrors);
    if (errorKeys.length > 0) {
      // No per-field helper text (to keep the compact Bond Order layout), so
      // surface the specific issues in the footer instead.
      const labelByKey = Object.fromEntries(editableFields.map((f) => [f.key, f.label]));
      const summary = errorKeys
        .map((key) => `${labelByKey[key]}: ${validationErrors[key]}`)
        .join("; ");
      setSubmitError(`Please correct the highlighted fields — ${summary}`);
      return false;
    }

    const payload = buildModifiedPayload(securityData, formValues, editableFields);
    if (Object.keys(payload).length === 0) {
      setSubmitError("No changes to update.");
      return false;
    }

    const secMasterId = resolveSecMasterId(securityData);
    if (secMasterId === null || secMasterId === "") {
      setSubmitError("Unable to update: this security has no identifier to update against.");
      return false;
    }

    setLoading(true);
    setSubmitError(null);
    try {
      const response = await updateSecurityMaster(token, secMasterId, payload);
      if (onUpdated) onUpdated(response, payload);
      if (onClose) onClose();
      return true;
    } catch (error) {
      console.error("Failed to update security:", error);
      setSubmitError(error.message || "Failed to update security. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [validateAll, securityData, formValues, editableFields, token, onUpdated, onClose]);

  const hasChanges = useMemo(
    () => Object.keys(buildModifiedPayload(securityData, formValues, editableFields)).length > 0,
    [securityData, formValues, editableFields]
  );

  return {
    formValues,
    errors,
    submitError,
    loading,
    editableFields,
    disabledFields,
    hasChanges,
    handleChange,
    handleSubmit,
  };
};
